import React, { useState, useEffect } from "react";
import { createMaterial, updateMaterial } from "../../services/apiService";
import {
  MATERIAL_CLASS_OPTIONS,
  buildSuggestedMaterialName,
  formatPriceAge,
  isPriceStale,
} from "../../utils/materialFlow";
import "./MaterialForm.css";

function MaterialForm({ onMaterialAdded, materialToEdit = null, onCancel }) {
  const [name, setName] = useState("");
  const [materialClass, setMaterialClass] = useState("");
  const [thicknessIn, setThicknessIn] = useState("");
  const [costPerSqft, setCostPerSqft] = useState("");
  const [sheetLengthIn, setSheetLengthIn] = useState("");
  const [sheetWidthIn, setSheetWidthIn] = useState("");
  const [costPerSheet, setCostPerSheet] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const isEditing = !!materialToEdit;

  // Pre-fill form when editing
  useEffect(() => {
    if (materialToEdit) {
      setName(materialToEdit.name || "");
      setMaterialClass(materialToEdit.material_class || "");
      setThicknessIn(materialToEdit.thickness_in ?? "");
      setCostPerSqft(materialToEdit.cost_per_sqft || "");
      setSheetLengthIn(materialToEdit.sheet_length_in || "");
      setSheetWidthIn(materialToEdit.sheet_width_in || "");
      setCostPerSheet(materialToEdit.cost_per_sheet || "");
    }
  }, [materialToEdit]);

  // Derived-name hint (never auto-applied): offered once class + thickness are
  // set and the current name doesn't already match the suggestion.
  const suggestedName = buildSuggestedMaterialName(materialClass, thicknessIn);
  const showNameSuggestion =
    Boolean(suggestedName) && suggestedName !== name.trim();

  const priceUpdatedAt = materialToEdit?.price_updated_at || null;
  const priceAgeLabel = formatPriceAge(priceUpdatedAt);
  const priceIsStale = isPriceStale(priceUpdatedAt);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    const costSqft = Number(costPerSqft);
    const lengthIn = sheetLengthIn ? Number(sheetLengthIn) : null;
    const widthIn = sheetWidthIn ? Number(sheetWidthIn) : null;
    const costSheet = costPerSheet ? Number(costPerSheet) : null;

    // Basic validation
    if (!name || isNaN(costSqft) || costSqft < 0) {
      setError(
        "Material Name and a valid non-negative Cost per SqFt are required.",
      );
      setIsSubmitting(false);
      return;
    }

    if (
      (sheetLengthIn && (isNaN(lengthIn) || lengthIn < 0)) ||
      (sheetWidthIn && (isNaN(widthIn) || widthIn < 0)) ||
      (costPerSheet && (isNaN(costSheet) || costSheet < 0))
    ) {
      setError(
        "If provided, sheet dimensions and cost must be valid non-negative numbers.",
      );
      setIsSubmitting(false);
      return;
    }

    const materialData = {
      name,
      material_class: materialClass || null,
      thickness_in: thicknessIn === "" ? null : Number(thicknessIn),
      cost_per_sqft: costSqft,
      sheet_length_in: lengthIn,
      sheet_width_in: widthIn,
      cost_per_sheet: costSheet,
    };

    try {
      if (isEditing) {
        await updateMaterial(materialToEdit.id, materialData);
      } else {
        await createMaterial(materialData);
      }

      // Clear form
      setName("");
      setMaterialClass("");
      setThicknessIn("");
      setCostPerSqft("");
      setSheetLengthIn("");
      setSheetWidthIn("");
      setCostPerSheet("");

      onMaterialAdded(); // Notify parent to refresh
    } catch (err) {
      setError(
        err.message || `Failed to ${isEditing ? "update" : "create"} material.`,
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <form onSubmit={handleSubmit} className="material-form">
      <h3>{isEditing ? "Edit Material" : "Add New Material"}</h3>
      {error && <div className="form-error">{error}</div>}

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="materialName">
            Material Name<span className="required">*</span>
          </label>
          <input
            type="text"
            id="materialName"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            placeholder='e.g., PVC Sheet 3/4"'
          />
          {showNameSuggestion && (
            <div className="name-suggestion">
              <span className="name-suggestion-text">
                Suggested name:{" "}
                <span className="name-suggestion-value">{suggestedName}</span>
              </span>
              <button
                type="button"
                className="wb-plain name-suggestion-apply"
                onClick={() => setName(suggestedName)}
              >
                Use suggested name
              </button>
            </div>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="costPerSqft">
            Cost per SqFt ($)<span className="required">*</span>
          </label>
          <input
            type="number"
            id="costPerSqft"
            value={costPerSqft}
            onChange={(e) => setCostPerSqft(e.target.value)}
            required
            step="0.0001"
            min="0"
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label htmlFor="materialClass">Material Class</label>
          <select
            id="materialClass"
            value={materialClass}
            onChange={(e) => setMaterialClass(e.target.value)}
          >
            <option value="">Unclassified</option>
            {MATERIAL_CLASS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <span className="helper-text">
            Groups this sheet for rate-card class resolution.
          </span>
        </div>

        <div className="form-group">
          <label htmlFor="thicknessIn">Thickness (in)</label>
          <input
            type="number"
            id="thicknessIn"
            value={thicknessIn}
            onChange={(e) => setThicknessIn(e.target.value)}
            step="0.001"
            min="0"
            placeholder="e.g., 0.5"
          />
          <span className="helper-text">Sheet thickness in inches</span>
        </div>

        {isEditing && (
          <div className="form-group">
            <label>Price updated</label>
            <span
              className={`price-updated-value${
                priceIsStale ? " price-updated-stale" : ""
              }`}
              title={priceIsStale ? "Price may be stale" : undefined}
            >
              {priceAgeLabel || "—"}
            </span>
          </div>
        )}
      </div>

      <h4>Sheet Information (Optional)</h4>
      <div className="form-row">
        <div className="form-group">
          <label htmlFor="sheetLengthIn">Sheet Length (in)</label>
          <input
            type="number"
            id="sheetLengthIn"
            value={sheetLengthIn}
            onChange={(e) => setSheetLengthIn(e.target.value)}
            step="0.01"
            min="0"
            placeholder="e.g., 96"
          />
          <span className="helper-text">Length in inches</span>
        </div>

        <div className="form-group">
          <label htmlFor="sheetWidthIn">Sheet Width (in)</label>
          <input
            type="number"
            id="sheetWidthIn"
            value={sheetWidthIn}
            onChange={(e) => setSheetWidthIn(e.target.value)}
            step="0.01"
            min="0"
            placeholder="e.g., 48"
          />
          <span className="helper-text">Width in inches</span>
        </div>

        <div className="form-group">
          <label htmlFor="costPerSheet">Cost Per Sheet ($)</label>
          <input
            type="number"
            id="costPerSheet"
            value={costPerSheet}
            onChange={(e) => setCostPerSheet(e.target.value)}
            step="0.01"
            min="0"
            placeholder="0.00"
          />
          <span className="helper-text">Total sheet cost</span>
        </div>
      </div>

      <div className="form-actions">
        <button type="submit" disabled={isSubmitting} className="submit-button">
          {isSubmitting
            ? "Saving..."
            : isEditing
              ? "Update Material"
              : "Save Material"}
        </button>
        {isEditing && (
          <button
            type="button"
            onClick={handleCancel}
            className="cancel-button"
            disabled={isSubmitting}
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  );
}

export default MaterialForm;
